import { getNodeId } from '../store/actionHandlers/common/adapterActions';
import HierarchySelectionBase from './common/HierarchySelectionBase';

const SELECTED = {
  selected: true,
  someChildrenSelected: true,
  allChildrenSelected: true,
};

const DESELECTED = {
  selected: false,
  someChildrenSelected: false,
  allChildrenSelected: false,
};

export default class HierarchySelection extends HierarchySelectionBase {
  prepareTreeState(treeState) {
    treeState.selection = [];
    treeState.multiselectStatuses = new Map();
  }

  handleDidAddModels(treeState, addedModels, { adapter }) {
    let changed = false;

    for (const node of addedModels) {
      const nodeId = getNodeId(adapter, node);

      if (hasSelectedParent(nodeId, treeState)) {
        if (!changed) {
          treeState.multiselectStatuses = new Map(
            treeState.multiselectStatuses,
          );
          changed = true;
        }
        treeState.multiselectStatuses.set(nodeId, SELECTED);
      }
    }
  }

  setSelection(treeState, selection, { adapter }) {
    treeState.selection = selection;
    treeState.multiselectStatuses = new Map();

    for (const node of selection) {
      const nodeId = getNodeId(adapter, node);
      treeState.multiselectStatuses.set(nodeId, SELECTED);
    }

    for (const node of selection) {
      const nodeId = getNodeId(adapter, node);
      recomputeParents(
        nodeId,
        treeState.multiselectStatuses,
        treeState.parents,
        treeState.children,
      );
    }
  }

  nodeIsSelected(treeState, nodeId) {
    return (
      this.nodeIsSelectable(treeState.models[nodeId], treeState.selection) &&
      multiselectStatus(nodeId, treeState.multiselectStatuses).selected
    );
  }

  nodeIsIntermediate(treeState, nodeId) {
    const { selected, someChildrenSelected } = multiselectStatus(
      nodeId,
      treeState.multiselectStatuses,
    );
    return !selected && someChildrenSelected;
  }

  toggleNodeSelected(oldTreeState, nodeId) {
    if (this.nodeIsSelected(oldTreeState, nodeId)) {
      return {
        ...oldTreeState,
        ...deselectNode(oldTreeState, nodeId),
      };
    }
    return {
      ...oldTreeState,
      ...selectNode(oldTreeState, nodeId),
    };
  }
}

function hasSelectedParent(nodeId, { parents, multiselectStatuses }) {
  let parentId = parents[nodeId];

  while (parentId) {
    if (multiselectStatus(parentId, multiselectStatuses).selected) {
      return true;
    }
    parentId = parents[parentId];
  }

  return false;
}

function selectNode(
  {
    models,
    parents,
    children,
    selection: oldSelection,
    multiselectStatuses: oldMultiselectStatuses,
  },
  nodeId,
) {
  const multiselectStatuses = new Map(oldMultiselectStatuses);

  setChildrenSelection(nodeId, multiselectStatuses, children, SELECTED);

  recomputeParents(nodeId, multiselectStatuses, parents, children);

  return {
    selection: collectSelection(
      models,
      multiselectStatuses,
      children,
      parents,
      oldSelection,
    ),
    multiselectStatuses,
  };
}

function deselectNode(
  {
    models,
    parents,
    children,
    multiselectStatuses: oldMultiselectStatuses,
    selection: oldSelection,
  },
  nodeId,
) {
  const multiselectStatuses = new Map(oldMultiselectStatuses);

  setChildrenSelection(nodeId, multiselectStatuses, children, null);

  recomputeParents(nodeId, multiselectStatuses, parents, children);

  return {
    selection: collectSelection(
      models,
      multiselectStatuses,
      children,
      parents,
      oldSelection,
    ),
    multiselectStatuses,
  };
}

function setChildrenSelection(nodeId, multiselectStatuses, children, status) {
  if (status) {
    multiselectStatuses.set(nodeId, status);
  } else {
    multiselectStatuses.delete(nodeId);
  }

  const childIds = children[nodeId];
  if (Array.isArray(childIds)) {
    for (const childId of childIds) {
      setChildrenSelection(childId, multiselectStatuses, children, status);
    }
  }
}

function recomputeParents(nodeId, multiselectStatuses, parents, children) {
  const parentId = parents[nodeId];
  if (!parentId) {
    return;
  }

  const parentStatus = multiselectStatus(parentId, multiselectStatuses);

  const allChildrenSelected = children[parentId].every(id => {
    const statuses = multiselectStatus(id, multiselectStatuses);
    return statuses.selected && statuses.allChildrenSelected;
  });

  const someChildrenSelected = children[parentId].some(id => {
    const statuses = multiselectStatus(id, multiselectStatuses);
    return statuses.selected || statuses.someChildrenSelected;
  });

  const selected = parentStatus.selected && allChildrenSelected;

  if (
    parentStatus.allChildrenSelected !== allChildrenSelected ||
    parentStatus.someChildrenSelected !== someChildrenSelected
  ) {
    multiselectStatuses.set(parentId, {
      selected,
      allChildrenSelected,
      someChildrenSelected,
    });
    recomputeParents(parentId, multiselectStatuses, parents, children);
  }
}

function collectSelection(
  models,
  multiselectStatuses,
  children,
  parents,
  oldSelection,
) {
  const result = [];

  for (const [id, status] of multiselectStatuses) {
    const parentId = parents[id];
    let parentSelected = false;

    if (parentId && multiselectStatus(parentId, multiselectStatuses).selected) {
      parentSelected = true;
    }

    if (!parentSelected && status.selected) {
      result.push(models[id] || oldSelection.find(org => org.id === id));
    }
  }

  return result.filter(node => !!node);
}

function multiselectStatus(nodeId, multiselectStatuses) {
  return multiselectStatuses.get(nodeId) || DESELECTED;
}
